import { memo, useEffect, useMemo, useState } from "react";
import {
  IActionGroupRatings,
  IActionGroupSkill,
  IOutletAuth,
  ISkill,
} from "types";
import {
  Card,
  PromptModal,
  Button,
  BorderBadge,
} from "components";
import { ReactComponent as Like } from "assets/images/like.svg";
import { ReactComponent as Dislike } from "assets/images/dislike.svg";
import { isNil } from "lodash";
import classnames from "classnames";
import { useQueryClient } from "react-query";
import { APP_ROUTES, ENDPOINTS } from "const";
import { useClickOnLikeDislikeActionGroup, useUpdateGroupRating } from "hooks";
import AvatarPlaceholder from "assets/images/avatar.png";
import { useNavigate, useOutletContext } from "react-router-dom";
import { Tooltip } from "react-tooltip";
import { LocationMarkerIcon, UsersIcon } from "@heroicons/react/outline";

interface IProps {
  title: string;
  skills?: Partial<IActionGroupSkill<Partial<ISkill>>>[] | undefined;
  profileImage: string | undefined;
  groupImage: string | undefined;
  createdAt: string | undefined;
  likes: number;
  dislikes: number;
  location?: string;
  forceHoverIssue?: boolean;
  membersNumber?: string;
  rate?: IActionGroupRatings | undefined | null;
  groupId: string;
  setOpenCardModal: React.Dispatch<React.SetStateAction<boolean>>;
  setHoveredIssueType: React.Dispatch<
    React.SetStateAction<"regular" | "city" | "country" | undefined>
  >;
  setIsIssueHovered?: React.Dispatch<React.SetStateAction<boolean>>;
  setIssueHoveredProps?: React.Dispatch<
    React.SetStateAction<{
      lat: number;
      lng: number;
    }>
  >;
  geoLocation: {
    lat: number | undefined;
    lng: number | undefined;
  };

  innerRef?: (node?: Element | null | undefined) => void;
  tags?: string[] | undefined;
  allLocations?: (string | null | undefined)[];
  isSoftDeleted?: boolean;
}

function GroupCard({
  title,
  profileImage,
  groupImage,
  createdAt = "",
  likes,
  membersNumber,
  dislikes,
  groupId,
  setOpenCardModal,
  setHoveredIssueType,
  setIsIssueHovered,
  setIssueHoveredProps,
  forceHoverIssue,
  geoLocation,
  rate,
  skills,
  location,
  innerRef,
  tags,
  allLocations,
  isSoftDeleted,
}: IProps) {
  const date = useMemo(() => {
    return (
      createdAt &&
      new Date(createdAt).toLocaleDateString("en-GB", {
        year: "numeric",
        month: "long",
        day: "2-digit",
      })
    );
  }, [createdAt]);
  const isLikesDislikes = useMemo(() => {
    return !isNil(likes) && !isNil(dislikes);
  }, [likes, dislikes]);

  const queryClient = useQueryClient();
  const invalidate = () => {
    queryClient.invalidateQueries([ENDPOINTS.ACTION_GROUPS]);
  };
  const { mutateAsync: mutateUpdateSolutionAsync, isLoading: updateLoading } =
    useUpdateGroupRating();
  const isLoading = updateLoading;
  const [rates, setRates] = useState({
    likes: 0,
    dislikes: 0,
    liked: false,
    disliked: false,
  });
  useEffect(() => {
    if (likes) {
      setRates((val) => ({ ...val, likes: likes }));
    }
    if (dislikes) {
      setRates((val) => ({ ...val, dislikes: dislikes }));
    }
    if (rate?.rate === 1) {
      setRates((val) => ({ ...val, liked: true, disliked: false }));
    }
    if (rate?.rate === 0) {
      setRates((val) => ({ ...val, liked: false, disliked: true }));
    }
  }, [likes, dislikes, rate]);
  const like = useClickOnLikeDislikeActionGroup(
    groupId,
    rate?.rate,
    mutateUpdateSolutionAsync,
    invalidate
  );
  const authContext = useOutletContext<IOutletAuth>();
  const [showGoSignInModal, setShowGoSignInModal] = useState(false);
  const navigate = useNavigate();

  const uniqueLocations = useMemo(() => {
    return [...new Set(allLocations)];
  }, [allLocations]);
  return (
    <>
      <Card
        customClickEventsOnCardAreas={{
          headerBlock: () => {
            setOpenCardModal(true);
          },
          dividersBlock: () => {
            setOpenCardModal(true);
          },
          contentBlock: () => {
            setOpenCardModal(true);
          },
        }}
        shouldHover={true}
        className={classnames("maxmd:flex-1 !gap-3 !py-5 h-full", {
          "bg-gray-100 select-none": isSoftDeleted,
        })}
        setHoveredIssueType={setHoveredIssueType}
        forceHoverIssue={forceHoverIssue}
        setIsIssueHovered={setIsIssueHovered}
        setIssueHoveredProps={setIssueHoveredProps}
        geoLocation={geoLocation}
        showHeaderDivider={false}
        showSubHeaderDivider={false}
        showContent={false}
        showFooterDivider={true}
        header={
          <div className="flex flex-col gap-2">
            {/* Title - Prominent */}
            <h3
              className="font-semibold text-base leading-6 line-clamp-2"
              ref={innerRef}
              style={{
                color: "#1C1917",
              }}
            >
              {title}
            </h3>

            {/* Location */}
            <div
              className="flex items-center gap-1 text-stone-400"
              data-tooltip-id={
                !!uniqueLocations && uniqueLocations?.length > 1
                  ? "locations_for_" + groupId
                  : ""
              }
            >
              <LocationMarkerIcon className="h-3.5 w-3.5 shrink-0" />
              <span
                className="text-xs truncate"
                style={{ fontFamily: "'Source Sans 3', sans-serif" }}
              >
                {location}
                {!!uniqueLocations &&
                  uniqueLocations?.length > 1 &&
                  ` +${uniqueLocations?.length - 1}`}
              </span>
            </div>
            <Tooltip
              id={"locations_for_" + groupId}
              place="top"
              className="white-tooltip font-semibold !rounded-md !p-2 z-30 max-w-[260px]"
            >
              <ul className="list-disc pl-4">
                {uniqueLocations?.map((loc, index) => (
                  <li key={index}>{loc}</li>
                ))}
              </ul>
            </Tooltip>

            {/* Skills Badges */}
            <div className="flex flex-wrap items-center gap-2">
              {skills?.slice(0, 2).map((skill) => (
                <BorderBadge
                  key={skill.id}
                  classificationName={skill?.skill?.name}
                  variant="outline"
                >
                  {skill?.skill?.name}
                </BorderBadge>
              ))}
              {skills && skills?.length > 2 && (
                <span
                  className="text-xs text-stone-400"
                  style={{ fontFamily: "'Source Sans 3', sans-serif" }}
                >
                  +{skills.length - 2} more
                </span>
              )}
              {(!skills || skills.length < 1) && (
                <span
                  className="text-xs text-stone-400 italic"
                  style={{ fontFamily: "'Source Sans 3', sans-serif" }}
                >
                  No skills assigned
                </span>
              )}
            </div>
          </div>
        }
        classNames={{
          header: "!gap-0 !pt-0",
          subHeader: "hidden",
          content: "flex-grow",
          footer: "!pt-3 !pb-0",
        }}
        footer={
          <div className="flex items-center justify-between w-full">
            {/* Left side: Avatar + Member count + Date */}
            <div className="flex items-center gap-3">
              {/* Small group avatar */}
              <div className="relative">
                <div className="h-10 w-10 rounded-full overflow-hidden border border-stone-200">
                  <img
                    src={groupImage || AvatarPlaceholder}
                    alt=""
                    className="h-full w-full object-cover"
                  />
                </div>
                {/* Creator avatar overlay */}
                <div className="absolute -bottom-0.5 -right-0.5 h-5 w-5 rounded-full overflow-hidden border-2 border-white">
                  <img
                    src={profileImage || AvatarPlaceholder}
                    alt=""
                    className="h-full w-full object-cover"
                  />
                </div>
              </div>

              <div className="flex flex-col">
                {/* Member count */}
                <div className="flex items-center gap-1 text-stone-500">
                  <UsersIcon className="h-3.5 w-3.5" />
                  <span
                    className="text-xs"
                    style={{ fontFamily: "'Source Sans 3', sans-serif" }}
                  >
                    {membersNumber} {Number(membersNumber) === 1 ? "member" : "members"}
                  </span>
                </div>
                {/* Date */}
                <span
                  className="text-xs text-stone-400"
                  style={{ fontFamily: "'Source Sans 3', sans-serif" }}
                >
                  {date}
                </span>
              </div>
            </div>

            {/* Right side: Like/Dislike buttons */}
            {isLikesDislikes && (
              <div
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                }}
                className="flex items-center gap-2"
              >
                {/* Like button */}
                <button
                  onClick={() => {
                    if (authContext.isAuth) {
                      if (!isLoading) {
                        if (rates.liked) {
                          setRates((val) => ({
                            ...val,
                            liked: false,
                            likes: val.likes - 1,
                          }));
                        } else {
                          setRates((val) => ({
                            ...val,
                            liked: true,
                            likes: val.likes + 1,
                            dislikes: val.dislikes > 0 ? val.dislikes - 1 : val.dislikes,
                            disliked: false,
                          }));
                        }
                        like(true);
                      }
                    } else {
                      setShowGoSignInModal(true);
                    }
                  }}
                  className={classnames(
                    "flex items-center gap-1 px-2 py-1 rounded-full text-xs transition-all",
                    {
                      "bg-stone-100 hover:bg-stone-200": !rates.liked,
                      "bg-stone-600 text-white": rates.liked,
                    }
                  )}
                  style={{ fontFamily: "'Source Sans 3', sans-serif" }}
                >
                  <Like
                    className="h-3.5 w-3.5"
                    stroke={rates.liked ? "white" : "#78716C"}
                  />
                  <span>{rates.likes}</span>
                </button>

                {/* Dislike button */}
                <button
                  onClick={() => {
                    if (authContext.isAuth) {
                      if (!isLoading) {
                        if (rates.disliked) {
                          setRates((val) => ({
                            ...val,
                            disliked: false,
                            dislikes: val.dislikes - 1,
                          }));
                        } else {
                          setRates((val) => ({
                            ...val,
                            liked: false,
                            likes: val.likes > 0 ? val.likes - 1 : val.likes,
                            dislikes: val.dislikes + 1,
                            disliked: true,
                          }));
                        }
                        like(false);
                      }
                    } else {
                      setShowGoSignInModal(true);
                    }
                  }}
                  className={classnames(
                    "flex items-center gap-1 px-2 py-1 rounded-full text-xs transition-all",
                    {
                      "bg-stone-100 hover:bg-stone-200": !rates.disliked,
                      "bg-stone-600 text-white": rates.disliked,
                    }
                  )}
                  style={{ fontFamily: "'Source Sans 3', sans-serif" }}
                >
                  <Dislike
                    className="h-3.5 w-3.5"
                    stroke={rates.disliked ? "white" : "#78716C"}
                  />
                  <span>{rates.dislikes}</span>
                </button>
              </div>
            )}
          </div>
        }
        tags={
          tags &&
          tags?.length > 0 && (
            <div className="flex items-center flex-wrap gap-1.5 px-6 pt-2">
              {tags?.map((tag) => (
                <BorderBadge key={tag} classificationName={tag} variant="tag">
                  {tag}
                </BorderBadge>
              ))}
            </div>
          )
        }
      />
      {showGoSignInModal && (
        <PromptModal
          setShow={setShowGoSignInModal}
          header="Warning"
          content="You have to sign in to perform this action"
          footer={
            <div className="flex flex-row gap-2 justify-between w-full">
              <Button
                inGroupStyle={false}
                isStyleless
                isReverse
                className=" shrink-0 text-[black] border hover:border-black border-gray-500 "
                onClick={() => {
                  setShowGoSignInModal(false);
                  setTimeout(() => {
                    navigate(APP_ROUTES.SIGN_IN);
                  }, 100);
                }}
              >
                Ok
              </Button>
              <Button
                inGroupStyle={false}
                isStyleless
                isReverse
                className=" text-[black] border hover:border-black border-gray-500 "
                onClick={(e) => {
                  e.stopPropagation();
                  setShowGoSignInModal(false);
                }}
              >
                Maybe later
              </Button>
            </div>
          }
        />
      )}
    </>
  );
}

export default memo(GroupCard);
